import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { Dropdown } from '@factorialco/factorial-one-react/dist/experimental'
import { Cross, Delete, Download, File } from '@factorialco/factorial-one-react/icons/app'
import {
  AtsJobApplicationCancelOfferLetterDocument,
  AtsJobApplicationDeleteOfferLetterDocument,
  AtsJobApplicationFilesOfferLetterItemFragment,
  AtsJobApplicationUpdateOfferLetterNotificationReadDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import DocumentType from 'modules/core/components/DocumentType'
import Modal from 'modules/core/components/Modal'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { OfferLetterStatusTag } from 'modules/ats/components/OfferLetterStatusTag'

interface Props {
  offerLetter: AtsJobApplicationFilesOfferLetterItemFragment
  applicationId: number
  jobPostingId: number
}

export const OfferLetterItem = ({ offerLetter, applicationId, jobPostingId }: Props) => {
  const { navigation } = useNavigation()
  const [openedModal, setOpenedModal] = useState<'delete' | 'cancel' | undefined>(undefined)

  const { id, createdAt, status, document, access: sentBy, notificationRead } = offerLetter

  const [deleteOfferLetter, { loading: deleting }] = useMutation(
    AtsJobApplicationDeleteOfferLetterDocument,
    {
      extractorFn: (data) => data.atsMutations.deleteAtsOfferLetter,
    }
  )

  const [cancelOfferLetter, { loading: cancelling }] = useMutation(
    AtsJobApplicationCancelOfferLetterDocument,
    {
      extractorFn: (data) => data.atsMutations.cancelAtsOfferLetter,
    }
  )

  const [updateNotificationRead] = useMutation(
    AtsJobApplicationUpdateOfferLetterNotificationReadDocument,
    {
      extractorFn: (data) => data.atsMutations.updateAtsOfferLetter,
    }
  )

  useAsyncData(async () => {
    if (!notificationRead) {
      await updateNotificationRead({ variables: { id, notificationRead: true } })
    }
  }, [])

  const onDelete = async () => {
    const result = await deleteOfferLetter({
      variables: {
        id,
      },
      update(cache) {
        cache.evict({ id: cache.identify(offerLetter) })
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.offer_letter.modal.success_deleted_message'),
      })

      setOpenedModal(undefined)
    }

    return handled
  }

  const onCancel = async () => {
    const result = await cancelOfferLetter({
      variables: {
        id,
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.offer_letter.modal.success_canceled_message'),
      })

      setOpenedModal(undefined)
    }

    return handled
  }

  const label = i18n.t('ats.candidates.files.offer_letter.offer_info', {
    created_at: moment(createdAt).format('ll'),
    hiring_manager: sentBy?.fullName ?? '',
  })

  return (
    <>
      <Box
        flexDirection="row"
        gap="s2"
        paddingX="s16"
        paddingY="s12"
        justifyContent="spaceBetween"
        width="full"
        alignItems="center"
        borderRadius={{ all: 'abs016' }}
        border={{
          all: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
      >
        <Box alignItems="center" flexGrow flexDirection="row" gap="s16">
          <DocumentType mimetype={document.contentType} />
          <Box overflowX="hidden" maxWidth="s300" gap="s4">
            <Text.Normal size="200" weight="medium" color="textPrimary">
              {document.filename}
            </Text.Normal>

            <Text.Normal size="200">{label}</Text.Normal>
          </Box>
        </Box>

        <Box flexDirection="row" gap="s12" justifyContent="flexEnd" alignItems="center">
          <OfferLetterStatusTag status={status} />
          <Dropdown
            items={[
              {
                label: i18n.t('ats.candidates.files.offer_letter.see_offer_letter'),
                href: navigation.ats.jobs.jobsId.applications.applicationDetail.offerLetter.fullPath(
                  {
                    jobId: jobPostingId,
                    application_id: applicationId,
                    offer_letter_id: id,
                  }
                ),
                icon: File,
              },
              status === 'pending_signature' && {
                label: i18n.t('ats.candidates.files.offer_letter.cancel_offer_letter_request'),
                icon: Cross,
                onClick: () => setOpenedModal('cancel'),
              },
              {
                label: i18n.t('documents.download'),
                href: document.url,
                icon: Download,
              },
              {
                label: i18n.t('documents.delete'),
                icon: Delete,
                onClick: () => setOpenedModal('delete'),
                danger: true,
              },
            ].filter(Boolean)}
          />
        </Box>
      </Box>

      {openedModal === 'delete' && (
        <Modal
          title={i18n.t('ats.candidates.files.offer_letter.delete_modal.title')}
          description={i18n.t('ats.candidates.files.offer_letter.delete_modal.description')}
          illustration="delete"
          onClose={() => setOpenedModal(undefined)}
          onSubmit={onDelete}
          footer={
            <Button type="negative" disabled={deleting} submit>
              {i18n.t('ats.candidates.files.offer_letter.delete_modal.submit')}
            </Button>
          }
        />
      )}

      {openedModal === 'cancel' && (
        <Modal
          title={i18n.t('ats.candidates.files.offer_letter.cancel_modal.title')}
          description={i18n.t('ats.candidates.files.offer_letter.cancel_modal.description')}
          illustration="delete-cross"
          onClose={() => setOpenedModal(undefined)}
          onSubmit={onCancel}
          footer={
            <Button type="secondary" disabled={cancelling} submit>
              {i18n.t('ats.candidates.files.offer_letter.cancel_modal.submit')}
            </Button>
          }
        />
      )}
    </>
  )
}
